import { Controller } from 'react-hook-form';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/shared/shadcn/ui/select';

export interface FieldConfig {
  visible?: boolean;
  required?: boolean;
  fieldId?: string;
}

export interface SelectOption {
  value: string;
  label: string;
}

interface ConfigurableSelectFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  config: FieldConfig | undefined;
  placeholder: string;
  options: Array<SelectOption>;
  label?: string;
  className?: string;
}

export function ConfigurableSelectField<TFieldValues extends FieldValues>({
  control,
  name,
  config,
  placeholder,
  options,
  label,
  className = 'text-gray-700 ring-0 focus:border-blue-500 focus:ring-0',
}: ConfigurableSelectFieldProps<TFieldValues>) {
  if (!config?.visible) {
    return null;
  }

  const displayPlaceholder = `${placeholder}${config.required ? ' *' : ''}`;

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: config.required ? `${placeholder} is required` : false,
      }}
      render={({ field, fieldState: { error } }) => (
        <div className='flex flex-col gap-1'>
          {label && (
            <label className='text-sm font-medium'>
              {label}
              {config.required ? ' *' : ''}
            </label>
          )}
          <Select onValueChange={field.onChange} value={field.value}>
            <SelectTrigger className={className}>
              <SelectValue placeholder={displayPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          {error && (
            <p className='text-[0.8rem] font-medium text-destructive'>
              {error.message}
            </p>
          )}
        </div>
      )}
    />
  );
}
